import React, { useEffect, useRef } from 'react';
import './Intro.css';
import logo from './logo1.png'; 

const Intro = ({ onEnter, exiting }) => {
  const canvasRef = useRef(null);
  const exitingRef = useRef(false);

  useEffect(() => {
    exitingRef.current = exiting;
  }, [exiting]);

  const handleInteraction = () => {
    if (!exiting) onEnter(); 
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let width, height;

    const particleCount = 2000; 
    const particles = [];
    const tubeRadius = 220; 
    const tubeThickness = 70; 
    const twistSpeed = 2.5; 
    
    let currentRotationSpeed = 0.0018; 
    let currentDriftSpeed = 0.4; 

    const colors = ['rgba(0, 224, 255, ', 'rgba(64, 224, 208, ', 'rgba(240, 248, 255, ', 'rgba(200, 230, 255, '];

    class Particle {
      constructor() { this.reset(true); }
      reset(initial = false) {
        this.strandOffset = Math.random() < 0.5 ? 0 : Math.PI;
        this.y = (Math.random() - 0.5) * window.innerHeight * 3;
        const theta = Math.random() * Math.PI * 2;
        const r = Math.random() * tubeThickness; 
        this.offsetX = Math.cos(theta) * r;
        this.offsetZ = Math.sin(theta) * r;
        this.speedMod = 0.8 + Math.random() * 0.4;
        this.wobblePhase = Math.random() * Math.PI * 2;
        this.size = Math.random() * 1.8; 
        this.colorPrefix = colors[Math.floor(Math.random() * colors.length)];
        this.alpha = 0.1 + Math.random() * 0.5;
      }
      update(time, currentDrift) {
        this.y -= currentDrift * this.speedMod;
        const limit = window.innerHeight * 1.5;
        if (this.y < -limit) this.y = limit;
        const phase = (this.y / 320) * twistSpeed;
        const angle = phase + this.strandOffset + time;
        const helixX = Math.cos(angle) * tubeRadius;
        const helixZ = Math.sin(angle) * tubeRadius;
        const wobbleX = Math.sin(time * 2 + this.wobblePhase) * 5;
        const wobbleZ = Math.cos(time * 1.5 + this.wobblePhase) * 5;
        this.x = helixX + this.offsetX + wobbleX;
        this.z = helixZ + this.offsetZ + wobbleZ;
        const tiltAngle = -0.35; 
        const tiltedX = this.x * Math.cos(tiltAngle) - this.y * Math.sin(tiltAngle);
        const tiltedY = this.x * Math.sin(tiltAngle) + this.y * Math.cos(tiltAngle);
        this.finalX = tiltedX;
        this.finalY = tiltedY;
        this.finalZ = this.z;
        this.alpha += Math.sin(time * 5 + this.wobblePhase) * 0.005;
      }
      draw(context, centerX, centerY) {
        const fov = 350;
        const scale = fov / (fov + 400 - this.finalZ);
        const x2d = this.finalX * scale + centerX;
        const y2d = this.finalY * scale + centerY;
        const s2d = this.size * scale;
        let depthAlpha = this.alpha;
        if (this.finalZ > 150) depthAlpha *= 0.3; 
        else if (this.finalZ > 0) depthAlpha *= 0.8;
        if (s2d > 0) {
          context.beginPath();
          context.arc(x2d, y2d, s2d, 0, Math.PI * 2);
          context.fillStyle = this.colorPrefix + Math.max(0, Math.min(1, depthAlpha)) + ')';
          context.fill();
        }
      }
    }

    const init = () => { resize(); for (let i = 0; i < particleCount; i++) { particles.push(new Particle()); } };
    const resize = () => { width = window.innerWidth; height = window.innerHeight; canvas.width = width; canvas.height = height; };
    let time = 0;
    
    const animate = () => {
      // CLEAR
      ctx.fillStyle = 'rgba(0, 0, 0, 0.25)'; 
      ctx.fillRect(0, 0, width, height);

      // --- PERFORMANCE OPTIMIZATION ---
      // If we are exiting, we don't need to be as precise with math
      if (exitingRef.current) {
         currentRotationSpeed *= 0.90; // Slow down faster
         currentDriftSpeed *= 0.90; 
      }

      ctx.globalCompositeOperation = 'lighter';
      const centerX = width / 2;
      const centerY = height / 2;
      time += currentRotationSpeed;

      particles.forEach(p => {
        p.update(time, currentDriftSpeed);
        p.draw(ctx, centerX, centerY);
      });
      
      ctx.globalCompositeOperation = 'source-over';
      animationFrameId = requestAnimationFrame(animate);
    };

    window.addEventListener('resize', resize);
    init();
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  // --- CHANGED: removed 'camera-push', added simple 'fade-out-container'
  return (
    <div className={`app-container ${exiting ? 'fade-out-container' : ''}`} onClick={handleInteraction}>
      <canvas ref={canvasRef} className="canvas-background" />
      <div className="grain-overlay"></div>
      <div className="ambient-light"></div>
      <div className="vignette"></div>

      <div className={`content-layer ${exiting ? 'blur-out-text' : ''}`}>
        <div className="logo-container">
           <img src={logo} alt="GoPT Logo" className="logo-image" />
        </div>
        <h1 className="tagline-main">Move Better, Live Better</h1>
        <h2 className="tagline-sub">Technology Enhanced Physiotherapy</h2>
      </div>

      <div className={`interaction-prompt ${exiting ? 'fade-quick' : ''}`}>
        [ Click anywhere to continue ]
      </div>
    </div>
  );
};

export default Intro;